<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useTap } from '@/composables/game/tap'
import TapBackImage from '@/assets/images/tap-back-image.png'
import TapFrontImage from '@/assets/images/gifs/duck-money-eye.gif'
import { DateTime } from 'luxon'
import api from '@/services/api'
import type { PlayerStats } from '@/types/shared/player'
import { useConfig } from '@/composables/game/config'
import { debounce } from '@/services/helper'
import { twMerge } from 'tailwind-merge'
import { useAnalytics } from '@/composables/analytics'
import { isiOS } from '@/helpers/browser'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { formatNumberFullTruncate } from '@/helpers/utils'
import FlashIconPNG from '@/components/icons/FlashIconGIF.vue'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import router from '@/router'
import { RouteName } from '@/routes'
import ShopButton from '@/components/ShopButton.vue'
import PuzzlesButton from '@/components/PuzzlesButton.vue'
import BoostButton from '@/components/BoostButton.vue'
import ChallengeButton from '@/components/ChallengeButton.vue'
import LimitedPackButton from '@/components/LimitedPackButton.vue'
import BundleButton from '@/components/BundleButton.vue'
import MysteryBoxButton from '@/components/MysteryBoxButton.vue'
import SkinsButton from '@/components/SkinsButton.vue'
import PathwayEventButton from '@/components/PathwayEventButton.vue'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import DailyUpChallengeButton from '@/components/DailyUpChallengeButton.vue'
import type { FrontendPathwayEvent } from '@/composables/game/pathway-events'
import LuckyRushButton from '@/components/LuckyRushButton.vue'
import PathwayCompletionChallengeButton from '@/components/PathwayCompletionChallengeButton.vue'
import MemoryChallengeButton from '@/components/MemoryChallengeButton.vue'
import PuzzleChallengeButton from '@/components/PuzzleChallengeButton.vue'
import { useWindowSize } from '@vueuse/core'
import ButtonsScrollContainer from '@/components/ButtonsScrollContainer.vue'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import MinesweeperChallengeButton from '@/components/MinesweeperChallengeButton.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import CollectibleChallengeButton from '@/components/CollectibleChallengeButton.vue'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'

const props = withDefaults(defineProps<{
	hideActions?: boolean
	isCipher?: boolean
	isCipherDone?: boolean
	backImage?: string
	frontImage?: string
	imageWrapperClass?: string
	imageInnerWrapperClass?: string
	tapContainerClass?: string
	backImageClass?: string
	frontImageClass?: string
	fadeImageClass?: string
	hideInnerImage?: boolean
	animate?: boolean
	shop?: {
		hasBundleButton: boolean
		hasShopButton: boolean
		showStarterPack: boolean
		shopCooldown: number
	}
	showLuckyRush?: boolean
	showBoost?: boolean
	showChallenge?: boolean
	showDailyUpChallenge?: boolean
	showPathwayCompletionChallenge?: boolean
	showMemoryChallenge?: boolean
	showMinesweeperChallenge?: boolean
	showSpecialCollectibleChallenge?: boolean
	showOnboardingEmoji?: boolean
	activeBoostsCount?: number
	customText?: string
	showSkins?: boolean
	showMysteryBox?: boolean
	showLimitedPack?: boolean
	pathwayEvents?: FrontendPathwayEvent[]
	showPuzzleChallenge?: boolean
	showPuzzle?: boolean
}>(), {
  hideActions: false,
  isCipher: false,
  isCipherDone: false,
  imageWrapperClass: '',
  imageInnerWrapperClass: '',
  tapContainerClass: '',
  backImageClass: '',
  frontImageClass: '',
  fadeImageClass: '',
  hideInnerImage: false,
  animate: false,
  shop: undefined,
  showBoost: false,
  showOnboardingEmoji: false,
  activeBoostsCount: 0,
  showSkins: false,
  showMysteryBox: false,
  showLimitedPack: false,
  showLuckyRush: false,
  pathwayEvents: () => [],
  backImage: () => TapBackImage,
  frontImage: () => TapFrontImage,
  customText: undefined,
})

const emit = defineEmits<{
	(e: 'isTapping', isTapping: boolean): void
	(e: 'decipheredLettersChange', letters: string): void
	(e: 'cipherDone', playerStats: PlayerStats): void
	(e: 'onBundleClick'): void
	(e: 'onChallengeClick'): void
	(e: 'onDailyUpChallengeClick'): void
	(e: 'onPathwayCompletionChallengeClick'): void
	(e: 'onLimitedPackClick'): void
	(e: 'onPathwayEventClick', event: PathwayEvent): void
	(e: 'onPuzzlesClick'): void
	(e: 'onPuzzleChallengeClick'): void
}>()

type TapOptions = { long: boolean } | undefined

const { player } = usePlayer()
const { cipherConfig } = useConfig()
const { tap: _tap } = useTap()
const { addHapticFeedback } = useHapticFeedback()
const { height } = useWindowSize()
const { openMemoryChallengeModal } = useMemoryChallengeModal()
const { openMinesweeperChallengeModal } = useMinesweeperChallengeModal()
const { openCollectibleChallengeModal } = useCollectibleChallengeModal()

const showTapOverlay = ref(false)
const isTapping = ref(false)
const tapHorizontalSide = ref<'left' | 'right'>('left')
const tapContainer = ref<HTMLElement | null>(null)
const { triggerEvent } = useAnalytics()
const { canShowOnboardingEmoji, showOnboardingEmojiTapSection } = useOnboardingFingerEmojis()

//for cipher:
const taps = ref<('-' | '.')[]>([])
const decipheredLetters = ref('')
const isCheckingCipher = ref(false)
const cipherSize = computed(() => cipherConfig.value.size)

function createFloatingNumber(x: number, y: number, text: string, longTap: boolean, isCustomText = false) {
  const numberElement = document.createElement('div')
  const backgroundElement = document.createElement('div')
  const textElement = document.createElement('div')

  // Apply styles to the outer div
  numberElement.className = 'floating-number'
  numberElement.style.left = x + 'px'
  numberElement.style.top = (y + window.scrollY) + 'px'
  if (!isiOS()) {
    numberElement.style.mixBlendMode = 'difference'
  }

  // Background element
  backgroundElement.className = 'floating-background'

  // Text element
  textElement.className = 'floating-number-text'
  textElement.style.fontSize = props.isCipher ? (longTap ? '40px' : '66px') : '24px'
  textElement.innerText = props.isCipher ? (longTap ? '_' : '.') : (isCustomText ? text : `+${text}`)

  // Append elements in the correct order
  numberElement.appendChild(backgroundElement)
  numberElement.appendChild(textElement)

  // Append the numberElement to the body
  document.body.appendChild(numberElement)

  // Remove element after animation ends
  numberElement.addEventListener('animationend', function() {
    numberElement.remove()
  })
}

const enableScroll = debounce(() => {
  isTapping.value = false
}, 400)

function disableScroll() {
  isTapping.value = true
  enableScroll()
}

function getTapPositions(event: TouchEvent | MouseEvent) {
  if ('x' in event) {
    return [{
      x: event.x,
      y: event.y,
    }]
  } else {
    const positions: { x: number, y: number }[] = []
    for (const touch of event.changedTouches) {
      positions.push({
        x: touch.clientX,
        y: touch.clientY,
      })
    }

    return positions
  }
}

const clickableButtons = ['tap_section_actions', 'left-buttons-container', 'right-buttons-container']

function checkForTapPrevent(event: TouchEvent) {
  const targetElement = (event.srcElement ?? event.target) as Element
  if (!targetElement) return null
  for (const button of clickableButtons) {
    if (targetElement.closest(`#${button}`)) {
      return button
    }
  }
  return null
}

async function tap(event: TouchEvent, options?: TapOptions) {
  showOnboardingEmojiTapSection.value = false
  let clickedElement = checkForTapPrevent(event)
  if (clickedElement) {
    event.stopPropagation()
    if (clickedElement === 'bundle_button') {
      event.preventDefault()
      emit('onBundleClick')
    }
    return
  }
  showTapOverlay.value = true
  if (event.cancelable) {
    event.stopPropagation()
    event.preventDefault()
  }

  const positions = getTapPositions(event)
  disableScroll()
  const validTapCount = _tap(positions.length)
  if (validTapCount === 0) return

  addHapticFeedback('medium')
  if (positions[0]) {
    setTapHorizontalSide(positions[0].x)
  }

  for (const position of positions.slice(0, validTapCount)) {
    const isLongTap = options?.long ?? false
    if (props.isCipher) {
      taps.value.push(isLongTap ? '-' : '.')
    }

    // Determine if custom text should be displayed instead
    const showCustomText = props.customText ? Math.random() < 0.3 : false // 30% chance to display the given custom text
    createFloatingNumber(position.x, position.y, showCustomText && props.customText ? props.customText : formatNumberFullTruncate(player.value.tap.earnPerTap * player.value.tap.coinPerUnit), isLongTap, showCustomText)
  }

  if (!props.isCipher || props.isCipherDone) return
  await checkTapsForCipher()
}

function longTap(event: TouchEvent) {
  tap(event, { long: true })
}

async function checkTapsForCipher() {
  if (taps.value.length > 0 && (typeof cipherSize.value[decipheredLetters.value.length] !== 'undefined') && taps.value.length >= cipherSize.value[decipheredLetters.value.length] && taps.value.length <= 10 && !isCheckingCipher.value) {
    isCheckingCipher.value = true
    try {
      const response = await api.cipher(decipheredLetters.value, taps.value.join(''))
      decipheredLetters.value = response.decipheredLetters
      if (decipheredLetters.value.length) {
        addHapticFeedback()
      }
      if (response.successful) {
        triggerEvent({ event: 'cipher_done' })
        emit('cipherDone', response.playerStats)
      }
    } finally {
      isCheckingCipher.value = false
      taps.value = []
    }
  } else {
    const tapsLength = taps.value.length
    setTimeout(() => {
      if (tapsLength === taps.value.length) {
        taps.value = []
        decipheredLetters.value = ''
      }
    }, 2000)
  }
}

const touchStartDate = ref(DateTime.now())

function press() {
  if (!props.isCipher) return
  touchStartDate.value = DateTime.now()
}

function release(event: TouchEvent) {
  if (!props.isCipher) return
  if (DateTime.now().toJSDate().getTime() > touchStartDate.value.plus({ second: 1 }).toJSDate().getTime()) {
    tap(event, { long: true })
  }
}

function setTapHorizontalSide(clickPosition: number) {
  if (tapContainer.value) {
    let rect = tapContainer.value.getBoundingClientRect()
    let x = clickPosition - rect.left // X coordinate of the click relative to the image
    let imageCenter = rect.width / 2
    tapHorizontalSide.value = x < imageCenter ? 'left' : 'right'
  }
}

watch(showTapOverlay, () => {
  if (!showTapOverlay.value) {
    return
  }
  setTimeout(() => showTapOverlay.value = false, 100)
})

watch(isTapping, isTapping => emit('isTapping', isTapping))

watch(decipheredLetters, decipheredLetters => {
  emit('decipheredLettersChange', decipheredLetters)
})
</script>

<template>
  <div class="flex h-full overflow-hidden">
    <div
      ref="tapContainer"
      :class="[{'touch-none': isTapping, 'scale-in': animate }, twMerge('h-full flex z-[10] relative items-center justify-between', tapContainerClass)]"
      v-touch:tap="tap"
      v-touch:longtap="longTap"
      v-touch:press="press"
      v-touch:release="release"
    >
      <img
        v-if="showOnboardingEmoji && canShowOnboardingEmoji && showOnboardingEmojiTapSection"
        class="pointer-events-none absolute right-0 bottom-0 size-[148px] rotate-[-45deg] object-cover z-[1]"
        :src="FingerUpGif"
      >

      <div
        id="left-buttons-container"
        class="pointer-events-auto z-[20] flex flex-col h-full gap-y-3 overflow-x-visible"
      >
        <ButtonsScrollContainer :visible-count="height < 690 ? 2 : (height < 760 ? 3 : (height < 830 ? 4 : 5))">
          <ShopButton
            v-if="!!shop && shop.hasShopButton"
            @click="addHapticFeedback('light');router.push({name: RouteName.SHOP})"
          />
          <BundleButton
            v-if="!!shop && shop.hasBundleButton"
            :cool-down="shop.shopCooldown"
            :label="shop.showStarterPack ? 'Starter' : 'Turbo Pack'"
            :is-starter="shop.showStarterPack"
            @click="addHapticFeedback('light');emit('onBundleClick')"
          />
          <LimitedPackButton
            v-if="showLimitedPack"
            @click="addHapticFeedback('light');emit('onLimitedPackClick')"
          />
          <MysteryBoxButton
            v-if="showMysteryBox"
            @click="addHapticFeedback('light');router.push({name: RouteName.MYSTERY_BOX})"
          />
          <BoostButton
            v-if="showBoost"
            :counter="activeBoostsCount"
            @click="addHapticFeedback('light');router.push({name: RouteName.BOOST})"
          />
          <SkinsButton
            v-if="showSkins"
            @click="addHapticFeedback('light');router.push({name: RouteName.SKINS})"
          />
        </ButtonsScrollContainer>
      </div>
      <div :class="twMerge('cursor-pointer flex items-center w-full rounded-full h-full', imageWrapperClass)">
        <div
          class="w-full h-full flex flex-col items-center justify-end pb-5"
          :class="showTapOverlay ? (tapHorizontalSide === 'left' ? 'left-transform-image' : 'right-transform-image') : ''"
        >
          <div :class="twMerge('h-full flex flex-col items-center justify-end rounded-full', imageInnerWrapperClass)">
            <div class="max-h-full w-full flex items-end justify-center overflow-hidden">
              <img
                :class="twMerge('h-full w-auto object-contain', backImageClass)"
                :src="backImage"
              >
            </div>
            <div
              v-if="!hideActions"
              id="tap_section_actions"
              class=" w-full flex items-center justify-center"
              @click.prevent.stop
            >
              <div class="flex items-center gap-x-1">
                <FlashIconPNG class="w-6 h-6" />
                <div class="text-base leading-4 font-semibold flex items-center text-light-white">
                  <span>{{ player.energy.available }}</span>
                  <span>&nbsp;/&nbsp;</span>
                  <span>{{ player.energy.max }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        id="right-buttons-container"
        class="pointer-events-auto z-[20] flex flex-col h-full gap-y-3 overflow-x-visible"
      >
        <ButtonsScrollContainer
          :gap="12"
          :visible-count="height < 710 ? 2 : (height < 790 ? 3 : (height < 870 ? 4 : 5))"
        >
          <CollectibleChallengeButton
            v-if="showSpecialCollectibleChallenge"
            @click="openCollectibleChallengeModal()"
          />
          <MemoryChallengeButton
            v-if="showMemoryChallenge"
            @click="openMemoryChallengeModal()"
          />
          <MinesweeperChallengeButton
            v-if="showMinesweeperChallenge"
            @click="openMinesweeperChallengeModal()"
          />
          <LuckyRushButton
            v-if="showLuckyRush"
            @click="router.push({name: RouteName.GIVEAWAY})"
          />
          <DailyUpChallengeButton
            v-if="showDailyUpChallenge"
            @click="$emit('onDailyUpChallengeClick')"
          />
          <PathwayCompletionChallengeButton
            v-if="showPathwayCompletionChallenge"
            @click="$emit('onPathwayCompletionChallengeClick')"
          />
          <PuzzleChallengeButton
            v-if="showPuzzleChallenge"
            @click="addHapticFeedback('light');emit('onPuzzleChallengeClick')"
          />
          <PuzzlesButton
            v-if="showPuzzle"
            @click="addHapticFeedback('light');emit('onPuzzlesClick')"
          />
          <PathwayEventButton
            v-for="pathwayEvent in pathwayEvents"
            :key="pathwayEvent.id"
            :id="`pathway_event_button_${pathwayEvent.id}`"
            :pathway-event="pathwayEvent"
            :class="{'hidden': pathwayEvent.hidden}"
            @click="addHapticFeedback('light'); emit('onPathwayEventClick', pathwayEvent)"
          />
          <ChallengeButton
            v-if="showChallenge"
            @click="$emit('onChallengeClick')"
          />
        </ButtonsScrollContainer>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.floating-number, .floating-cipher {
	@apply fixed z-[20] flex items-center justify-center h-[44px] min-w-[44px];
	animation: floatup 0.7s ease-in;
	font-family: "space grotesk variable", sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	pointer-events: none;
	touch-action: none;
}

.floating-background {
	@apply absolute inset-0 rounded-full bg-solid-white/[.12] backdrop-blur-[6px];
	pointer-events: none;
	touch-action: none;
}

.floating-number-text {
	@apply text-light-white z-[1] relative font-semibold;
	mix-blend-mode: difference;
	pointer-events: none;
	touch-action: none;
}

.floating-cipher {
	@apply pb-9;
}

@keyframes floatup {
	0% {
		opacity: 1;
		transform: translatey(0);
	}
	70% {
		opacity: 1;
	}
	100% {
		opacity: 0;
		transform: translatey(-150px);
	}
}

.left-transform-image {
	transform: perspective(1500px) rotatey(-10deg);
}

.right-transform-image {
	transform: perspective(1500px) rotatey(10deg);
}

.fade-circle-overlay {
	background: white;
	mix-blend-mode: overlay;
	filter: blur(24px);
}
</style>